import type { ReactNode } from 'react';
import { IfWrites } from '../components/write-access';
import { NewPersonLink } from '../components/people/new-person-link';
import { Link } from 'react-router-dom';
import { useNeedsAction, useSubjects } from '../api/hooks';
import { loadErrorLine } from '../api/errors';
import { NeedsAction } from '../components/needs-action/needs-action';
import { PeopleTable } from '../components/people/people-table';
import { ORG_SPEND_LIMIT_LINE, PEOPLE_EMPTY_BY_LEVEL, SEE_ALL_PEOPLE } from '../lib/copy';
import { formatWeekday } from '../lib/format';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { Section } from '../components/ui/section';
import './home.css';

/**
 * Home is the inbox (ui-design §4.1): Needs review, and a short read of who has
 * moved lately. No badge counts in the nav — the page is the inbox. The spend
 * line that used to sit in the foot is gone: research spend is admin-plane only
 * (amended 2026-08-27), and it is watched on the operator dash.
 *
 * **Amended 2026-09-06 (import note §1.18/§2.6).** The whole People table used
 * to live here, which was right at ten prospects a month and is wrong at 2,650:
 * a list nobody can look through is not an index. Home keeps the ten most
 * recently updated as a glance at what has moved, and the index itself is
 * `/people`, with search, status chips, tracked/known and paging. Deliberately
 * the *default* read — tracked people, most recent first — so the glance and
 * the first screen of `/people` are the same rows in the same order.
 */

/** How many rows are a glance rather than a list. */
const RECENT = 10;

export function Home() {
  const subjects = useSubjects({ limit: RECENT });

  return (
    <HomeView
      inbox={
        <IfWrites>
          <HomeInbox />
        </IfWrites>
      }
      people={
        subjects.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : subjects.isError ? (
          <EmptyLine>{loadErrorLine(subjects.error)}</EmptyLine>
        ) : subjects.data.entries.length === 0 ? (
          /* Home's glance is the *tracked* read, so its empty line is the
             tracked one — "No people yet." over an org holding four hundred
             known people would be the same untruth the People screen's was. */
          <EmptyLine>{PEOPLE_EMPTY_BY_LEVEL.tracked}</EmptyLine>
        ) : (
          <>
            <PeopleTable entries={subjects.data.entries} />
            <div className="list-foot">
              <Link className="link" to="/people">
                {SEE_ALL_PEOPLE}
              </Link>
            </div>
          </>
        )
      }
    />
  );
}

export function HomeView({ inbox, people }: { inbox?: ReactNode; people: ReactNode }) {
  return (
    <div className="column home">
      <div className="head">
        <div className="titles">
          <div className="title">Home</div>
          <div className="subline home-date">{formatWeekday(new Date())}</div>
        </div>
        <NewPersonLink />
      </div>
      {inbox}
      <Section title="People">{people}</Section>
    </div>
  );
}

/** Action queues and spend guidance belong only to writable organizations. */
function HomeInbox() {
  const needsAction = useNeedsAction();
  return (
    <>
      {/* Under the head and over everything the page asks for, because it is the
          reason some of those asks are being refused. */}
      {needsAction.data?.monthly_spend_ceiling_reached ? <SpendLimitBanner /> : null}

      <Section title="Needs review">
        {needsAction.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : needsAction.isError ? (
          <EmptyLine>{loadErrorLine(needsAction.error)}</EmptyLine>
        ) : (
          <NeedsAction
            entries={needsAction.data.entries}
            mergeCandidates={needsAction.data.merge_candidates}
            renameProposals={needsAction.data.rename_proposals}
            mergedAutomatically={needsAction.data.merged_automatically}
          />
        )}
      </Section>
    </>
  );
}

/**
 * **The organization has spent its month** (2026-09-09) — the office's whole
 * view of the allowance, and the only place it is stated without something
 * having just been refused.
 *
 * On Home rather than on the record pages, because it is a fact about the
 * organization rather than about a person, and Home is the page every reader
 * starts on. It is **not dismissable**, and that is not an oversight: a
 * dismissed banner would leave a reader pressing Research and meeting a refusal
 * with no memory of why. It goes away when the limit is raised or the month
 * turns over, which is exactly when it stops being true.
 *
 * The sentence is `errorLine`'s, out of one constant, so the bar at the top of
 * the page and the line under the button that was just refused say the same
 * thing. **No figure in it** — the limit is the operator's, and the office's
 * move is to ask.
 *
 * The blend's attention treatment: the one sentence between hairlines, in
 * copper, with no label over it (a label said the same thing twice), no tinted
 * box and no left rule. Copper rather than the brick: nothing is broken and
 * nothing was done wrong, but somebody is being asked to act.
 */
export function SpendLimitBanner() {
  return <p className="home-notice">{ORG_SPEND_LIMIT_LINE}</p>;
}
